// =============================================================================
// SystemSetupAdmin — system-level SETUP, tabbed, editable by an administrator
// =============================================================================
// The single home for "how the platform is wired up" settings. Organised into
// tabs (John, 23 Jul 2026):
//   • Alerts         — the System Alert email address (where the portal sends an
//                      alert when a notification email fails to send) + the
//                      read-only failed-notifications list (moved here from the
//                      System Content → "Monitoring" tab on 26 Jul 2026).
//   • Access         — role-group login lockout switches + message, plus the
//                      New Requests + Stock Top-ups availability switches (moved
//                      here from Config on 13 Aug 2026).
//   • Config         — next request number + auto-logout + per-device delivery.
//   • Feedback Module — the Feedback notification recipient + the standard
//                      "reviewed" reply text.
//   • School Locker  — order recipient, monitoring BCC, no-reply alert, polling.
//   • Unleashed      — the SO-push on/off switch, push-failure alert channels,
//                      and the Customer-sync source-of-truth.
//
// All values live in the `portal_config` table (one row per key). The System
// Alert email + Feedback settings previously lived on System Content →
// "System Alerts" (renamed "Monitoring", which still keeps the Monitored-service
// alert message). The config keys + value shapes + audit actions are UNCHANGED by
// the move — the Edge Functions that read them server-side (system_alert /
// feedback_new / school_locker_order) are unaffected.
// =============================================================================
import React from "react";
import {
  BYOD_BANDS_PROMPT_KEY,
  BYOD_PROMPT_KEY,
  DEFAULT_BYOD_BANDS_PROMPT,
  DEFAULT_BYOD_PROMPT,
  DEFAULT_THINKING_LEVEL,
  normalizePolicyReview,
  normalizeThinkingLevel,
  POLICY_REVIEW_KEY,
  THINKING_LEVEL_KEY,
} from "../../../supabase/functions/_shared/byod/prompt";
import {
  BYOD_RESPONDER_CAN_EDIT_KEY,
  BYOD_RESPONDER_WHOLE_SCHOOL_KEY,
  BYOD_SHOW_RESPONDERS_KEY,
  BYOD_SHOW_SHELTERS_KEY,
  SHOW_DEVICE_DELIVERY_KEY,
  STOCK_TOP_UP_ENABLED_KEY,
} from "../../app/UiConfigContext.jsx";
import {
  DEFAULT_FEEDBACK_REVIEW_TEMPLATE,
  FEEDBACK_REVIEW_TEMPLATE_KEY,
} from "../../data/feedback";
import { getConfigValue, upsertConfig } from "../../data/portalConfig";
import { getNextRefNoPreview, setNextRefNo } from "../../data/requests";
import {
  countPushableOrders,
  excludeAllPushableOrders,
  normalizePushAlerts,
  PUSH_ALERTS_KEY,
} from "../../data/salesOrderPushes";
import {
  ACCESS_LOCKOUT_KEY,
  DEFAULT_ACCESS_LOCKOUT_MESSAGE,
  normalizeAccessLockout,
} from "../../services/accessLockout";
import { CAPTCHA_SWITCH_KEY, captchaEnabled, captchaSwitchedOn } from "../../services/captcha";
import { confirmDialog } from "../../services/dialogService";
import { NEW_REQUESTS_KEY, normalizeNewRequestsEnabled } from "../../services/newRequestSwitch";
import {
  MAX_MINUTES,
  MIN_MINUTES,
  normalizeSessionTimeout,
  SESSION_TIMEOUT_KEY,
} from "../../services/sessionTimeout";
import { logAudit } from "../../utils/auditLog";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn, SlideToggle } from "../../utils/ui.jsx";
import { FailedNotificationsList } from "./FailedNotificationsList.jsx";
import { RepeatCheckSettings } from "./RepeatCheckSettings.jsx";
import { SectionAvailabilityAdmin } from "./SectionAvailabilityAdmin.jsx";

const SL_BCC_KEY = "school_locker_bcc";
const SL_ALERT_KEY = "school_locker_alert_email";
const SL_ORDER_KEY = "school_locker_order_email";
const API_ENABLED_KEY = "unleashed_api_enabled";
const CUSTOMER_SYNC_ENABLED_KEY = "unleashed_customer_sync_enabled";
const SYSTEM_ALERT_KEY = "system_alert_email";
const DEFAULT_ALERT_EMAIL = "admin@zephyreducation.com.au";
const FEEDBACK_NOTIFY_KEY = "feedback_notify_email";
const DEFAULT_FEEDBACK_EMAIL = "Isabella.Bevan@zephyreducation.com.au";

const TABS = [
  { key: "alerts", label: "Alerts" },
  { key: "access", label: "Access" },
  { key: "config", label: "Config" },
  { key: "feedback", label: "Feedback Module" },
  { key: "school_locker", label: "School Locker" },
  { key: "unleashed", label: "Unleashed" },
  { key: "ai_searches", label: "AI Searches" },
  { key: "section_availability", label: "Section Availability" },
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
}

// Tolerate a missing/absent key (returns { enabled:false, email:"" }) and an
// older bare-string shape, just in case.
function readBccValue(value) {
  if (value && typeof value === "object") {
    return { enabled: !!value.enabled, email: typeof value.email === "string" ? value.email : "" };
  }
  if (typeof value === "string" && value) return { enabled: true, email: value };
  return { enabled: false, email: "" };
}

// system_alert_email is stored as { email }; feedback_notify_email as a plain
// JSON string (db-23 seed). Tolerate either shape when reading.
function readEmailValue(value, fallback) {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object" && value.email) return value.email;
  return fallback;
}

// Shared Edit → Save/Cancel footer for the gated settings sections (John, 24 Aug
// 2026: every settings Save must be a conscious Edit-then-Save). While NOT editing
// it shows a single "Edit" button; while editing it shows Save + Cancel. Fields in
// the section are made read-only via `disabled={!editing}` by the caller. Mirrors
// the existing Next Request Number pattern.
function EditSaveFooter({ editing, onEdit, onSave, onCancel, saving, saveLabel = "Save", msg }) {
  // Any message that isn't the success "Saved ✓" is a failure/validation notice → red.
  const err = !!msg && !msg.startsWith("Saved");
  return (
    <div className="flex gap-[12px] mt-[14px] items-center">
      {!editing ? (
        <Btn onClick={onEdit}>Edit</Btn>
      ) : (
        <>
          <Btn onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : saveLabel}
          </Btn>
          <button
            type="button"
            onClick={onCancel}
            className="bg-white text-zephyr-text rounded-[8px] px-4 py-[7px] text-[13px] font-semibold cursor-pointer"
            style={{ border: "1px solid #CFE0DE" }}
          >
            Cancel
          </button>
        </>
      )}
      {msg && (
        <span className={`text-[13px] ${err ? "text-[#C62828]" : "text-[#2E7D32]"}`}>{msg}</span>
      )}
    </div>
  );
}

export function SystemSetupAdmin({ user }) {
  const [tab, setTab] = React.useState("alerts");

  // Alerts tab — System Alert email address.
  const [alertEmail, setAlertEmail] = React.useState("");
  const [alertSaving, setAlertSaving] = React.useState(false);
  const [alertMsg, setAlertMsg] = React.useState("");

  // Feedback Module tab — notification recipient + standard reply text.
  const [fbEmail, setFbEmail] = React.useState("");
  const [fbSaving, setFbSaving] = React.useState(false);
  const [fbMsg, setFbMsg] = React.useState("");
  const [reviewTpl, setReviewTpl] = React.useState("");
  const [reviewSaving, setReviewSaving] = React.useState(false);
  const [reviewMsg, setReviewMsg] = React.useState("");

  const [slEnabled, setSlEnabled] = React.useState(false);
  const [slEmail, setSlEmail] = React.useState("");
  const [slAlertEmail, setSlAlertEmail] = React.useState("");
  const [slAlertSaving, setSlAlertSaving] = React.useState(false);
  const [slAlertMsg, setSlAlertMsg] = React.useState("");
  const [slOrderEmail, setSlOrderEmail] = React.useState("");
  const [slOrderSaved, setSlOrderSaved] = React.useState(""); // last-saved value (Cancel reverts here)
  const [slOrderEditing, setSlOrderEditing] = React.useState(false);
  const [slOrderSaving, setSlOrderSaving] = React.useState(false);
  const [slOrderMsg, setSlOrderMsg] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [pollRunning, setPollRunning] = React.useState(false);
  const [pollMsg, setPollMsg] = React.useState("");
  const [apiEnabled, setApiEnabled] = React.useState(false);
  const [apiBusy, setApiBusy] = React.useState(false);
  const [apiMsg, setApiMsg] = React.useState("");
  const [alerts, setAlerts] = React.useState({
    email: true,
    banner: true,
    audit: true,
    runLog: true,
  });
  const [alertsSaving, setAlertsSaving] = React.useState(false);
  const [alertsMsg, setAlertsMsg] = React.useState("");
  const [custSyncEnabled, setCustSyncEnabled] = React.useState(false);
  const [custSyncBusy, setCustSyncBusy] = React.useState(false);
  const [custSyncMsg, setCustSyncMsg] = React.useState("");

  // Access tab — the role-group login lockout switches + the configurable message.
  // Config tab — the "+ New Request" switch. Starts enabled (fail-open).
  const [newRequests, setNewRequests] = React.useState(true);
  const [newRequestsMsg, setNewRequestsMsg] = React.useState("");
  const [lockout, setLockout] = React.useState(normalizeAccessLockout(null));
  const [lockoutSaving, setLockoutSaving] = React.useState(false);
  const [lockoutMsg, setLockoutMsg] = React.useState("");

  // Access tab — the login CAPTCHA widget kill switch (default ON / fail-open).
  const [captchaOn, setCaptchaOn] = React.useState(true);
  const [captchaBusy, setCaptchaBusy] = React.useState(false);
  const [captchaMsg, setCaptchaMsg] = React.useState("");

  // Config tab — the next request number (go-live seeding of the ref_no sequence).
  const [refPreview, setRefPreview] = React.useState("");
  const [refInput, setRefInput] = React.useState("");
  const [refSaving, setRefSaving] = React.useState(false);
  const [refMsg, setRefMsg] = React.useState("");
  const [refEditing, setRefEditing] = React.useState(false);

  // Config tab — auto-logout periods (minutes), separate for DFV Services vs staff.
  const [timeout, setTimeoutCfg] = React.useState(normalizeSessionTimeout(null));
  const [timeoutSaving, setTimeoutSaving] = React.useState(false);
  const [timeoutMsg, setTimeoutMsg] = React.useState("");

  // Config tab — whether the per-device "Delivery Address" section shows on Device
  // items (default OFF; each request carries a request-level delivery address).
  const [showDeviceDelivery, setShowDeviceDelivery] = React.useState(false);
  const [sddSaving, setSddSaving] = React.useState(false);
  const [sddMsg, setSddMsg] = React.useState("");

  // Config tab — whether Stock Top-ups are live for DFV Services. When OFF, a
  // stock-holding service still sees the New-Request chooser but the Stock Top-up
  // option is disabled and reads "Not available at the moment"; ON makes it live for
  // all. (TEST services get no special-case — treated like any other service, 13 Sep 2026.)
  const [stockTopUp, setStockTopUp] = React.useState(false);
  const [stuSaving, setStuSaving] = React.useState(false);
  const [stuMsg, setStuMsg] = React.useState("");

  // Edit/Save gating (John, 24 Aug 2026): each text/number settings section is
  // read-only until the operator clicks Edit, then Save (or Cancel to revert). One
  // `*Editing` flag per section + a ref snapshot captured at Edit-time so Cancel can
  // restore. (The instant on/off SWITCHES are unchanged — they save on toggle.)
  const [alertEditing, setAlertEditing] = React.useState(false);
  const alertSnap = React.useRef("");
  const [fbEditing, setFbEditing] = React.useState(false);
  const fbSnap = React.useRef("");
  const [reviewEditing, setReviewEditing] = React.useState(false);
  const reviewSnap = React.useRef("");
  const [slAlertEditing, setSlAlertEditing] = React.useState(false);
  const slAlertSnap = React.useRef("");
  const [bccEditing, setBccEditing] = React.useState(false);
  const bccSnap = React.useRef({ enabled: false, email: "" });
  const [alertsEditing, setAlertsEditing] = React.useState(false);
  const alertsSnap = React.useRef(null);
  const [lockoutEditing, setLockoutEditing] = React.useState(false);
  const lockoutSnap = React.useRef(null);
  const [timeoutEditing, setTimeoutEditing] = React.useState(false);
  const timeoutSnap = React.useRef(null);

  // AI Searches tab — the editable BYOD lookup prompt + the annual policy-review date.
  const [byodPrompt, setByodPrompt] = React.useState(DEFAULT_BYOD_PROMPT);
  const [byodPromptSaving, setByodPromptSaving] = React.useState(false);
  const [byodPromptMsg, setByodPromptMsg] = React.useState("");
  const [byodPromptEditing, setByodPromptEditing] = React.useState(false);
  const byodPromptSnap = React.useRef("");
  // The whole-school (multi-band) lookup prompt — the admin "Edit School" manager (Part 7).
  const [byodBandsPrompt, setByodBandsPrompt] = React.useState(DEFAULT_BYOD_BANDS_PROMPT);
  const [byodBandsPromptSaving, setByodBandsPromptSaving] = React.useState(false);
  const [byodBandsPromptMsg, setByodBandsPromptMsg] = React.useState("");
  const [byodBandsPromptEditing, setByodBandsPromptEditing] = React.useState(false);
  const byodBandsPromptSnap = React.useRef("");
  const [policyReview, setPolicyReview] = React.useState(normalizePolicyReview(null));
  const [policyReviewSaving, setPolicyReviewSaving] = React.useState(false);
  const [policyReviewMsg, setPolicyReviewMsg] = React.useState("");
  const [policyReviewEditing, setPolicyReviewEditing] = React.useState(false);
  const policyReviewSnap = React.useRef(null);
  const [byodThinking, setByodThinking] = React.useState(DEFAULT_THINKING_LEVEL);
  const [byodThinkingSaving, setByodThinkingSaving] = React.useState(false);
  const [byodThinkingMsg, setByodThinkingMsg] = React.useState("");
  const [byodThinkingEditing, setByodThinkingEditing] = React.useState(false);
  const byodThinkingSnap = React.useRef(DEFAULT_THINKING_LEVEL);
  // BYOD visibility switches (Stage 3) — each gates one placement; both default OFF.
  const [byodShowResp, setByodShowResp] = React.useState(false);
  const [byodShowRespSaving, setByodShowRespSaving] = React.useState(false);
  const [byodShowRespMsg, setByodShowRespMsg] = React.useState("");
  const [byodShowShel, setByodShowShel] = React.useState(false);
  const [byodShowShelSaving, setByodShowShelSaving] = React.useState(false);
  const [byodShowShelMsg, setByodShowShelMsg] = React.useState("");
  // Part 8 — whether responders may edit/save a BYOD result (ON) or only view it (OFF, default).
  const [byodRespEdit, setByodRespEdit] = React.useState(false);
  const [byodRespEditSaving, setByodRespEditSaving] = React.useState(false);
  const [byodRespEditMsg, setByodRespEditMsg] = React.useState("");
  // Part 9 — whether a responder lookup searches the whole school (ON) or the child's year (OFF, default).
  const [byodWholeSchool, setByodWholeSchool] = React.useState(false);
  const [byodWholeSchoolSaving, setByodWholeSchoolSaving] = React.useState(false);
  const [byodWholeSchoolMsg, setByodWholeSchoolMsg] = React.useState("");

  React.useEffect(function () {
    getConfigValue(SL_BCC_KEY).then(function (val) {
      const parsed = readBccValue(val);
      setSlEnabled(parsed.enabled);
      setSlEmail(parsed.email);
      setLoading(false);
    });
    getConfigValue(SYSTEM_ALERT_KEY).then(function (val) {
      setAlertEmail(readEmailValue(val, DEFAULT_ALERT_EMAIL));
    });
    getConfigValue(FEEDBACK_NOTIFY_KEY).then(function (val) {
      setFbEmail(readEmailValue(val, DEFAULT_FEEDBACK_EMAIL));
    });
    getConfigValue(FEEDBACK_REVIEW_TEMPLATE_KEY).then(function (val) {
      setReviewTpl(typeof val === "string" && val ? val : DEFAULT_FEEDBACK_REVIEW_TEMPLATE);
    });
    getConfigValue(SL_ALERT_KEY).then(function (val) {
      setSlAlertEmail(typeof val === "string" ? val : "");
    });
    getConfigValue(SL_ORDER_KEY).then(function (val) {
      const v = typeof val === "string" ? val : "";
      setSlOrderEmail(v);
      setSlOrderSaved(v);
    });
    getConfigValue(API_ENABLED_KEY).then(function (val) {
      setApiEnabled(val === true);
    });
    getConfigValue(PUSH_ALERTS_KEY).then(function (val) {
      setAlerts(normalizePushAlerts(val));
    });
    getConfigValue(CUSTOMER_SYNC_ENABLED_KEY).then(function (val) {
      setCustSyncEnabled(val === true);
    });
    getConfigValue(NEW_REQUESTS_KEY).then(function (val) {
      setNewRequests(normalizeNewRequestsEnabled(val));
    });
    getConfigValue(ACCESS_LOCKOUT_KEY).then(function (val) {
      setLockout(normalizeAccessLockout(val));
    });
    getConfigValue(CAPTCHA_SWITCH_KEY).then(function (val) {
      setCaptchaOn(captchaSwitchedOn(val));
    });
    getConfigValue(SESSION_TIMEOUT_KEY).then(function (val) {
      setTimeoutCfg(normalizeSessionTimeout(val));
    });
    getConfigValue(SHOW_DEVICE_DELIVERY_KEY).then(function (val) {
      setShowDeviceDelivery(val === true);
    });
    getConfigValue(STOCK_TOP_UP_ENABLED_KEY).then(function (val) {
      setStockTopUp(val === true);
    });
    getConfigValue(BYOD_PROMPT_KEY).then(function (val) {
      setByodPrompt(typeof val === "string" && val.trim() ? val : DEFAULT_BYOD_PROMPT);
    });
    getConfigValue(BYOD_BANDS_PROMPT_KEY).then(function (val) {
      setByodBandsPrompt(typeof val === "string" && val.trim() ? val : DEFAULT_BYOD_BANDS_PROMPT);
    });
    getConfigValue(POLICY_REVIEW_KEY).then(function (val) {
      setPolicyReview(normalizePolicyReview(val));
    });
    getConfigValue(THINKING_LEVEL_KEY).then(function (val) {
      setByodThinking(normalizeThinkingLevel(val));
    });
    getConfigValue(BYOD_SHOW_RESPONDERS_KEY).then(function (val) {
      setByodShowResp(val === true);
    });
    getConfigValue(BYOD_SHOW_SHELTERS_KEY).then(function (val) {
      setByodShowShel(val === true);
    });
    getConfigValue(BYOD_RESPONDER_CAN_EDIT_KEY).then(function (val) {
      setByodRespEdit(val === true);
    });
    getConfigValue(BYOD_RESPONDER_WHOLE_SCHOOL_KEY).then(function (val) {
      setByodWholeSchool(val === true);
    });
    getNextRefNoPreview().then(function (val) {
      if (val) {
        setRefPreview(val);
        setRefInput(val);
      }
    });
  }, []);

  // Accept "2026-00211", "00211", or "211" — the year prefix is applied by the DB,
  // so only the trailing digits matter. NaN if there are none.
  function parseRefNo(str) {
    const m = String(str || "")
      .trim()
      .match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : Number.NaN;
  }

  function saveNextRefNo() {
    const n = parseRefNo(refInput);
    if (!Number.isInteger(n) || n < 1 || n > 99999) {
      setRefMsg("Enter a number between 1 and 99999.");
      setTimeout(function () {
        setRefMsg("");
      }, 4000);
      return;
    }
    setRefSaving(true);
    setNextRefNo(n).then(function (r) {
      setRefSaving(false);
      if (r.error) {
        setRefMsg(`Save failed: ${r.error.message}`);
        return;
      }
      const shown = r.data || "";
      if (shown) {
        setRefPreview(shown);
        setRefInput(shown);
      }
      setRefEditing(false);
      setRefMsg("Saved ✓");
      logAudit("next_ref_no_set", { next_ref_no: shown || n, by: user?.shelterName });
      setTimeout(function () {
        setRefMsg("");
      }, 3000);
    });
  }

  function setTimeoutField(key, raw) {
    // Keep the raw string in state while typing; coerce on save.
    setTimeoutCfg(function (t) {
      return { ...t, [key]: raw };
    });
  }

  function saveTimeout() {
    const shelter = Number(timeout.shelter_minutes);
    const staff = Number(timeout.staff_minutes);
    const ok = (n) => Number.isInteger(n) && n >= MIN_MINUTES && n <= MAX_MINUTES;
    if (!ok(shelter) || !ok(staff)) {
      setTimeoutMsg(`Enter whole minutes between ${MIN_MINUTES} and ${MAX_MINUTES}.`);
      setTimeout(function () {
        setTimeoutMsg("");
      }, 4000);
      return;
    }
    setTimeoutSaving(true);
    upsertConfig(SESSION_TIMEOUT_KEY, { shelter_minutes: shelter, staff_minutes: staff }).then(
      function (r) {
        setTimeoutSaving(false);
        if (r.error) {
          setTimeoutMsg(`Save failed: ${r.error.message}`);
          return;
        }
        setTimeoutCfg({ shelter_minutes: shelter, staff_minutes: staff });
        setTimeoutMsg("Saved ✓");
        setTimeoutEditing(false);
        logAudit("session_timeout_updated", {
          shelter_minutes: shelter,
          staff_minutes: staff,
          by: user?.shelterName,
        });
        setTimeout(function () {
          setTimeoutMsg("");
        }, 3000);
      }
    );
  }

  function toggleShowDeviceDelivery(v) {
    setSddSaving(true);
    // Stored as a bare boolean (like unleashed_api_enabled); absent/false = hidden.
    upsertConfig(SHOW_DEVICE_DELIVERY_KEY, v).then(function (r) {
      setSddSaving(false);
      if (r.error) {
        setSddMsg(`Save failed: ${r.error.message}`);
        setTimeout(function () {
          setSddMsg("");
        }, 4000);
        return;
      }
      setShowDeviceDelivery(v);
      setSddMsg(v ? "Per-device delivery is now shown ✓" : "Per-device delivery is now hidden ✓");
      logAudit("show_device_delivery_toggled", { enabled: v, by: user?.shelterName });
      setTimeout(function () {
        setSddMsg("");
      }, 3000);
    });
  }

  function toggleStockTopUp(v) {
    setStuSaving(true);
    // Stored as a bare boolean (like show_device_delivery); absent/false = off.
    upsertConfig(STOCK_TOP_UP_ENABLED_KEY, v).then(function (r) {
      setStuSaving(false);
      if (r.error) {
        setStuMsg(`Save failed: ${r.error.message}`);
        setTimeout(function () {
          setStuMsg("");
        }, 4000);
        return;
      }
      setStockTopUp(v);
      setStuMsg(
        v
          ? "Stock Top-ups are now available to all DFV Services ✓"
          : 'Stock Top-ups are now off — the option reads "Not available at the moment" for stock-holding services ✓'
      );
      logAudit("stock_top_up_enabled_toggled", { enabled: v, by: user?.shelterName });
      setTimeout(function () {
        setStuMsg("");
      }, 3000);
    });
  }

  function toggleByodShowResp(v) {
    setByodShowRespSaving(true);
    // Stored as a bare boolean; absent/false = hidden (BYOD Stage 3).
    upsertConfig(BYOD_SHOW_RESPONDERS_KEY, v).then(function (r) {
      setByodShowRespSaving(false);
      if (r.error) {
        setByodShowRespMsg(`Save failed: ${r.error.message}`);
        setTimeout(function () {
          setByodShowRespMsg("");
        }, 4000);
        return;
      }
      setByodShowResp(v);
      setByodShowRespMsg(
        v
          ? "BYOD lookup is now shown to Responders ✓"
          : "BYOD lookup is now hidden from Responders ✓"
      );
      logAudit("byod_show_responders_toggled", { enabled: v, by: user?.shelterName });
      setTimeout(function () {
        setByodShowRespMsg("");
      }, 3000);
    });
  }

  function toggleByodShowShel(v) {
    setByodShowShelSaving(true);
    upsertConfig(BYOD_SHOW_SHELTERS_KEY, v).then(function (r) {
      setByodShowShelSaving(false);
      if (r.error) {
        setByodShowShelMsg(`Save failed: ${r.error.message}`);
        setTimeout(function () {
          setByodShowShelMsg("");
        }, 4000);
        return;
      }
      setByodShowShel(v);
      setByodShowShelMsg(
        v ? "BYOD lookup is now shown to Shelters ✓" : "BYOD lookup is now hidden from Shelters ✓"
      );
      logAudit("byod_show_shelters_toggled", { enabled: v, by: user?.shelterName });
      setTimeout(function () {
        setByodShowShelMsg("");
      }, 3000);
    });
  }

  function toggleByodRespEdit(v) {
    setByodRespEditSaving(true);
    // Stored as a bare boolean; absent/false = view-only (BYOD Part 8).
    upsertConfig(BYOD_RESPONDER_CAN_EDIT_KEY, v).then(function (r) {
      setByodRespEditSaving(false);
      if (r.error) {
        setByodRespEditMsg(`Save failed: ${r.error.message}`);
        setTimeout(function () {
          setByodRespEditMsg("");
        }, 4000);
        return;
      }
      setByodRespEdit(v);
      setByodRespEditMsg(
        v
          ? "Responders can now edit/save BYOD results ✓"
          : "Responders now see BYOD results read-only ✓"
      );
      logAudit("byod_responder_can_edit_toggled", { enabled: v, by: user?.shelterName });
      setTimeout(function () {
        setByodRespEditMsg("");
      }, 3000);
    });
  }

  function toggleByodWholeSchool(v) {
    setByodWholeSchoolSaving(true);
    // Stored as a bare boolean; absent/false = search the child's year only (Part 9).
    upsertConfig(BYOD_RESPONDER_WHOLE_SCHOOL_KEY, v).then(function (r) {
      setByodWholeSchoolSaving(false);
      if (r.error) {
        setByodWholeSchoolMsg(`Save failed: ${r.error.message}`);
        setTimeout(function () {
          setByodWholeSchoolMsg("");
        }, 4000);
        return;
      }
      setByodWholeSchool(v);
      setByodWholeSchoolMsg(
        v
          ? "Responder lookups now search the whole school ✓"
          : "Responder lookups now search the child's year only ✓"
      );
      logAudit("byod_responder_whole_school_toggled", { enabled: v, by: user?.shelterName });
      setTimeout(function () {
        setByodWholeSchoolMsg("");
      }, 3000);
    });
  }

  // The "+ New Request" switch (Config tab). Saves immediately on toggle — it's a
  // single boolean and an operator flipping it expects it to take effect, not to
  // need a separate Save.
  function saveNewRequests(enabled) {
    setNewRequests(enabled);
    upsertConfig(NEW_REQUESTS_KEY, enabled).then(function (r) {
      if (r.error) {
        setNewRequestsMsg(`Save failed: ${r.error.message}`);
        setNewRequests(!enabled); // put the switch back — it didn't save
        return;
      }
      setNewRequestsMsg(enabled ? "New requests enabled ✓" : "New requests turned off ✓");
      logAudit("new_requests_switch_updated", { enabled, by: user?.shelterName });
      setTimeout(function () {
        setNewRequestsMsg("");
      }, 3000);
    });
  }

  function setLockoutField(key, value) {
    setLockout(function (l) {
      return { ...l, [key]: value };
    });
  }

  function saveLockout() {
    setLockoutSaving(true);
    const payload = {
      shelter: !!lockout.shelter,
      picker: !!lockout.picker,
      responder: !!lockout.responder,
      supervisor: !!lockout.supervisor,
      message: (lockout.message || "").trim() || DEFAULT_ACCESS_LOCKOUT_MESSAGE,
    };
    upsertConfig(ACCESS_LOCKOUT_KEY, payload).then(function (r) {
      setLockoutSaving(false);
      if (r.error) {
        setLockoutMsg(`Save failed: ${r.error.message}`);
        return;
      }
      setLockoutMsg("Saved ✓");
      setLockoutEditing(false);
      logAudit("access_lockout_updated", {
        shelter: payload.shelter,
        picker: payload.picker,
        responder: payload.responder,
        supervisor: payload.supervisor,
        by: user?.shelterName,
      });
      setTimeout(function () {
        setLockoutMsg("");
      }, 3000);
    });
  }

  // The login CAPTCHA kill switch. Turning it OFF only HIDES the widget — the real
  // enforcement is Supabase's own toggle, so the confirm dialog makes the operator
  // acknowledge they turned Supabase off FIRST (otherwise every sign-in is rejected
  // and nobody can log in). Saves immediately on confirm.
  async function turnCaptchaOff() {
    const ok = await confirmDialog({
      title: "Turn off the login verification box (CAPTCHA)",
      message:
        "IMPORTANT - do the Supabase step FIRST. This switch only HIDES the verification box on the login screen; the actual checking happens in Supabase. If you hide the box while Supabase is still enforcing CAPTCHA, every sign-in is rejected and NOBODY can log in. First open Supabase -> Authentication -> Attack Protection, turn CAPTCHA protection OFF and Save. Only then continue here. Have you already turned CAPTCHA off in Supabase?",
      confirmLabel: "Yes - Supabase is off, hide the box",
      cancelLabel: "Cancel",
      danger: true,
    });
    if (!ok) return;
    setCaptchaBusy(true);
    const res = await upsertConfig(CAPTCHA_SWITCH_KEY, { enabled: false });
    setCaptchaBusy(false);
    if (res.error) {
      setCaptchaMsg(`Failed: ${res.error.message}`);
      return;
    }
    setCaptchaOn(false);
    setCaptchaMsg("Login verification box is now HIDDEN ✓");
    logAudit("captcha_switch_toggled", { enabled: false, by: user?.shelterName });
    setTimeout(function () {
      setCaptchaMsg("");
    }, 4000);
  }

  async function turnCaptchaOn() {
    const ok = await confirmDialog({
      title: "Turn the login verification box (CAPTCHA) back on",
      message:
        "Show the verification box on the login screen again. Remember to also turn CAPTCHA protection back ON in Supabase -> Authentication -> Attack Protection, otherwise the box appears but nothing is actually enforced.",
      confirmLabel: "Turn on",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    setCaptchaBusy(true);
    const res = await upsertConfig(CAPTCHA_SWITCH_KEY, { enabled: true });
    setCaptchaBusy(false);
    if (res.error) {
      setCaptchaMsg(`Failed: ${res.error.message}`);
      return;
    }
    setCaptchaOn(true);
    setCaptchaMsg("Login verification box is now SHOWN ✓");
    logAudit("captcha_switch_toggled", { enabled: true, by: user?.shelterName });
    setTimeout(function () {
      setCaptchaMsg("");
    }, 4000);
  }

  function saveSystemAlert() {
    const trimmed = (alertEmail || "").trim();
    if (!isValidEmail(trimmed)) {
      setAlertMsg("Please enter a valid email address.");
      setTimeout(function () {
        setAlertMsg("");
      }, 4000);
      return;
    }
    setAlertSaving(true);
    upsertConfig(SYSTEM_ALERT_KEY, { email: trimmed }).then(function (r) {
      setAlertSaving(false);
      if (r.error) {
        setAlertMsg(`Save failed: ${r.error.message}`);
      } else {
        setAlertMsg("Saved ✓");
        setAlertEditing(false);
        logAudit("system_alert_email_updated", { email: trimmed, by: user?.shelterName });
      }
      setTimeout(function () {
        setAlertMsg("");
      }, 3000);
    });
  }

  function saveFeedbackNotify() {
    const trimmed = (fbEmail || "").trim();
    if (!isValidEmail(trimmed)) {
      setFbMsg("Please enter a valid email address.");
      setTimeout(function () {
        setFbMsg("");
      }, 4000);
      return;
    }
    setFbSaving(true);
    // Stored as a plain string to match the db-23 seed + the Edge Function read.
    upsertConfig(FEEDBACK_NOTIFY_KEY, trimmed).then(function (r) {
      setFbSaving(false);
      if (r.error) {
        setFbMsg(`Save failed: ${r.error.message}`);
      } else {
        setFbMsg("Saved ✓");
        setFbEditing(false);
        logAudit("feedback_notify_email_updated", { email: trimmed, by: user?.shelterName });
      }
      setTimeout(function () {
        setFbMsg("");
      }, 3000);
    });
  }

  function saveReviewTemplate() {
    const text = (reviewTpl || "").trim();
    if (!text) {
      setReviewMsg("The reply text can't be empty.");
      setTimeout(function () {
        setReviewMsg("");
      }, 4000);
      return;
    }
    setReviewSaving(true);
    // Stored as a plain string (the Edge Function + the review modal read it as one).
    upsertConfig(FEEDBACK_REVIEW_TEMPLATE_KEY, text).then(function (r) {
      setReviewSaving(false);
      if (r.error) {
        setReviewMsg(`Save failed: ${r.error.message}`);
      } else {
        setReviewMsg("Saved ✓");
        setReviewEditing(false);
        logAudit("feedback_review_template_updated", { by: user?.shelterName });
      }
      setTimeout(function () {
        setReviewMsg("");
      }, 3000);
    });
  }

  function saveAlerts() {
    setAlertsSaving(true);
    upsertConfig(PUSH_ALERTS_KEY, alerts).then(function (r) {
      setAlertsSaving(false);
      if (r.error) {
        setAlertsMsg(`Save failed: ${r.error.message}`);
        return;
      }
      setAlertsMsg("Saved ✓");
      setAlertsEditing(false);
      logAudit("unleashed_push_alerts_updated", { ...alerts, by: user?.shelterName });
      setTimeout(function () {
        setAlertsMsg("");
      }, 3000);
    });
  }

  async function turnCustSyncOn() {
    const ok = await confirmDialog({
      title: "Turn on Unleashed customer auto-sync",
      message:
        "Turn on automatic customer sync? Changes to Zephyr DFV Services (details, bank details, delivery addresses) will be pushed to Unleashed automatically by the background worker. Any changes queued while it was off will be sent on the next run. TEST / non-Zephyr services are never auto-pushed.",
      confirmLabel: "Turn on",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    setCustSyncBusy(true);
    const res = await upsertConfig(CUSTOMER_SYNC_ENABLED_KEY, true);
    setCustSyncBusy(false);
    if (res.error) {
      setCustSyncMsg(`Failed: ${res.error.message}`);
      return;
    }
    logAudit("unleashed_customer_sync_toggled", { enabled: true, by: user?.shelterName });
    setCustSyncEnabled(true);
    setCustSyncMsg("Customer auto-sync is ON ✓");
    setTimeout(function () {
      setCustSyncMsg("");
    }, 3000);
  }

  async function turnCustSyncOff() {
    const ok = await confirmDialog({
      title: "Turn off Unleashed customer auto-sync",
      message:
        "Turn off automatic customer sync? Changes to DFV Services will stop being pushed to Unleashed. They keep queuing and will be sent when you turn it back on (nothing is lost).",
      confirmLabel: "Turn off",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    setCustSyncBusy(true);
    const res = await upsertConfig(CUSTOMER_SYNC_ENABLED_KEY, false);
    setCustSyncBusy(false);
    if (res.error) {
      setCustSyncMsg(`Failed: ${res.error.message}`);
      return;
    }
    logAudit("unleashed_customer_sync_toggled", { enabled: false, by: user?.shelterName });
    setCustSyncEnabled(false);
    setCustSyncMsg("Customer auto-sync is OFF ✓");
    setTimeout(function () {
      setCustSyncMsg("");
    }, 3000);
  }

  function flashSaved(msg, ms) {
    setSavedMsg(msg);
    setTimeout(function () {
      setSavedMsg("");
    }, ms || 3000);
  }

  // AI Searches — save the editable BYOD lookup prompt (stored as a plain string;
  // the Edge Function reads it and appends the fixed JSON-output contract).
  function saveByodPrompt() {
    const text = (byodPrompt || "").trim();
    if (!text) {
      setByodPromptMsg("The prompt can't be empty.");
      setTimeout(function () {
        setByodPromptMsg("");
      }, 4000);
      return;
    }
    setByodPromptSaving(true);
    upsertConfig(BYOD_PROMPT_KEY, text).then(function (r) {
      setByodPromptSaving(false);
      if (r.error) {
        setByodPromptMsg(`Save failed: ${r.error.message}`);
        return;
      }
      setByodPromptMsg("Saved ✓");
      setByodPromptEditing(false);
      logAudit("byod_lookup_prompt_updated", { by: user?.shelterName });
      setTimeout(function () {
        setByodPromptMsg("");
      }, 3000);
    });
  }

  // AI Searches — save the whole-school (multi-band) lookup prompt used by the admin
  // "Edit School" manager (the EF appends the fixed array JSON contract).
  function saveByodBandsPrompt() {
    const text = (byodBandsPrompt || "").trim();
    if (!text) {
      setByodBandsPromptMsg("The prompt can't be empty.");
      setTimeout(function () {
        setByodBandsPromptMsg("");
      }, 4000);
      return;
    }
    setByodBandsPromptSaving(true);
    upsertConfig(BYOD_BANDS_PROMPT_KEY, text).then(function (r) {
      setByodBandsPromptSaving(false);
      if (r.error) {
        setByodBandsPromptMsg(`Save failed: ${r.error.message}`);
        return;
      }
      setByodBandsPromptMsg("Saved ✓");
      setByodBandsPromptEditing(false);
      logAudit("byod_bands_lookup_prompt_updated", { by: user?.shelterName });
      setTimeout(function () {
        setByodBandsPromptMsg("");
      }, 3000);
    });
  }

  function setPolicyReviewField(key, value) {
    setPolicyReview(function (p) {
      return { ...p, [key]: value };
    });
  }

  // AI Searches — save the annual policy-review date (month/day). Normalised to a
  // real calendar day; drives which school year a lookup is stamped with (and,
  // later, staleness).
  function savePolicyReview() {
    const norm = normalizePolicyReview({
      month: Number(policyReview.month),
      day: Number(policyReview.day),
    });
    setPolicyReviewSaving(true);
    upsertConfig(POLICY_REVIEW_KEY, norm).then(function (r) {
      setPolicyReviewSaving(false);
      if (r.error) {
        setPolicyReviewMsg(`Save failed: ${r.error.message}`);
        return;
      }
      setPolicyReview(norm);
      setPolicyReviewMsg("Saved ✓");
      setPolicyReviewEditing(false);
      logAudit("byod_policy_review_updated", {
        month: norm.month,
        day: norm.day,
        by: user?.shelterName,
      });
      setTimeout(function () {
        setPolicyReviewMsg("");
      }, 3000);
    });
  }

  // AI Searches — save the AI thinking level (low/medium/high).
  function saveByodThinking() {
    const level = normalizeThinkingLevel(byodThinking);
    setByodThinkingSaving(true);
    upsertConfig(THINKING_LEVEL_KEY, level).then(function (r) {
      setByodThinkingSaving(false);
      if (r.error) {
        setByodThinkingMsg(`Save failed: ${r.error.message}`);
        return;
      }
      setByodThinking(level);
      setByodThinkingMsg("Saved ✓");
      setByodThinkingEditing(false);
      logAudit("byod_thinking_level_updated", { level, by: user?.shelterName });
      setTimeout(function () {
        setByodThinkingMsg("");
      }, 3000);
    });
  }

  function saveSlBcc() {
    const trimmed = (slEmail || "").trim();
    // If the BCC is ON, an email is required and must be valid. When OFF, the
    // address is irrelevant — allow saving to turn it off with a blank field.
    if (slEnabled && !isValidEmail(trimmed)) {
      flashSaved("Please enter a valid email address to receive the BCC copy.", 4000);
      return;
    }
    setSaving(true);
    upsertConfig(SL_BCC_KEY, { enabled: slEnabled, email: trimmed }).then(function (r) {
      setSaving(false);
      if (r.error) {
        flashSaved(`Save failed: ${r.error.message}`);
      } else {
        flashSaved("Saved ✓");
        setBccEditing(false);
        logAudit("school_locker_bcc_updated", {
          enabled: slEnabled,
          email: trimmed,
          by: user?.shelterName,
        });
      }
    });
  }

  function saveSlAlert() {
    const trimmed = (slAlertEmail || "").trim();
    if (!isValidEmail(trimmed)) {
      setSlAlertMsg("Please enter a valid email address.");
      setTimeout(function () {
        setSlAlertMsg("");
      }, 4000);
      return;
    }
    setSlAlertSaving(true);
    upsertConfig(SL_ALERT_KEY, trimmed).then(function (r) {
      setSlAlertSaving(false);
      if (r.error) {
        setSlAlertMsg(`Save failed: ${r.error.message}`);
      } else {
        setSlAlertMsg("Saved ✓");
        setSlAlertEditing(false);
        logAudit("school_locker_alert_email_updated", { email: trimmed, by: user?.shelterName });
        setTimeout(function () {
          setSlAlertMsg("");
        }, 3000);
      }
    });
  }

  function saveSlOrder() {
    const trimmed = (slOrderEmail || "").trim();
    if (!isValidEmail(trimmed)) {
      setSlOrderMsg("Please enter a valid email address.");
      setTimeout(function () {
        setSlOrderMsg("");
      }, 4000);
      return;
    }
    setSlOrderSaving(true);
    upsertConfig(SL_ORDER_KEY, trimmed).then(function (r) {
      setSlOrderSaving(false);
      if (r.error) {
        setSlOrderMsg(`Save failed: ${r.error.message}`);
      } else {
        setSlOrderMsg("Saved ✓");
        setSlOrderSaved(trimmed);
        setSlOrderEditing(false);
        logAudit("school_locker_order_email_updated", { email: trimmed, by: user?.shelterName });
        setTimeout(function () {
          setSlOrderMsg("");
        }, 3000);
      }
    });
  }

  // Manually trigger one poll now. Sends { manual:true } so the Edge Function
  // bypasses its operating-hours window + idle-skip and does a real check.
  async function runPollingNow() {
    setPollRunning(true);
    setPollMsg("");
    const supa = getSupabaseClient();
    if (!supa) {
      setPollRunning(false);
      setPollMsg("Run failed: could not reach the server.");
      return;
    }
    try {
      const { data, error } = await supa.functions.invoke("poll-school-locker", {
        body: { manual: true },
      });
      if (error) {
        setPollMsg(`Run failed: ${error.message}`);
      } else if (data && data.ok === false) {
        setPollMsg(`Run failed: ${data.error || "unknown error"}`);
      } else {
        const seen = data?.emails_seen || 0;
        const matched = data?.matches || 0;
        setPollMsg(`Done — checked ${seen} email(s), captured ${matched} code(s).`);
        logAudit("school_locker_poll_manual_run", { by: user?.shelterName });
      }
    } catch (e) {
      setPollMsg(`Run failed: ${e?.message || "error"}`);
    } finally {
      setPollRunning(false);
    }
  }

  // Turn the Unleashed API push ON. Sophisticated cutover: if orders are already
  // queued, let the admin choose to EXCLUDE them (they've been hand-imported —
  // recommended) or PUSH them too. Exclusion runs BEFORE the flag flips so the
  // worker can never pick up a pre-cutover order in the gap.
  async function turnApiOn() {
    const n = await countPushableOrders();
    let excludeExisting = false;
    if (n === 0) {
      const ok = await confirmDialog({
        title: "Turn on Unleashed API SO push",
        message:
          "Turn on the Unleashed API SO push? New sales orders will be sent to Unleashed automatically.",
        confirmLabel: "Turn on",
      });
      if (!ok) return;
    } else {
      const proceed = await confirmDialog({
        title: "Turn on Unleashed API SO push",
        message: `${n} sales order(s) are currently queued and not yet excluded. Turn the API on?`,
        confirmLabel: "Continue",
        cancelLabel: "Cancel",
      });
      if (!proceed) return;
      // Second choice: exclude those existing orders, or let the API push them.
      excludeExisting = await confirmDialog({
        title: "Existing queued orders",
        message: `Exclude the ${n} order(s) already queued from the API? Recommended if you have already imported them into Unleashed manually — they will NOT be pushed. Choose "Push them" to send them via the API.`,
        confirmLabel: "Exclude them",
        cancelLabel: "Push them",
      });
    }

    setApiBusy(true);
    setApiMsg("");
    if (excludeExisting) {
      const r = await excludeAllPushableOrders();
      if (r.error) {
        setApiBusy(false);
        setApiMsg(`Failed: ${r.error.message}`);
        return;
      }
      logAudit("sales_order_api_excluded", {
        scope: "cutover_all",
        count: n,
        by: user?.shelterName,
      });
    }
    const res = await upsertConfig(API_ENABLED_KEY, true);
    setApiBusy(false);
    if (res.error) {
      setApiMsg(`Failed: ${res.error.message}`);
      return;
    }
    setApiEnabled(true);
    setApiMsg("Unleashed API SO push is ON ✓");
    logAudit("unleashed_api_toggled", {
      enabled: true,
      excluded_existing: excludeExisting,
      queued_count: n,
      by: user?.shelterName,
    });
  }

  // Turn the Unleashed API push OFF. Queued orders simply accumulate until it's
  // turned back on.
  async function turnApiOff() {
    const ok = await confirmDialog({
      title: "Turn off Unleashed API SO push",
      message:
        "Turn off the Unleashed API SO push? Queued orders will stop being sent to Unleashed until you turn it back on.",
      confirmLabel: "Turn off",
    });
    if (!ok) return;
    setApiBusy(true);
    setApiMsg("");
    const res = await upsertConfig(API_ENABLED_KEY, false);
    setApiBusy(false);
    if (res.error) {
      setApiMsg(`Failed: ${res.error.message}`);
      return;
    }
    setApiEnabled(false);
    setApiMsg("Unleashed API SO push is OFF ✓");
    logAudit("unleashed_api_toggled", { enabled: false, by: user?.shelterName });
  }

  if (loading) return <div className="text-zephyr-muted p-5">Loading...</div>;

  return (
    <div className="p-6 max-w-[1100px]">
      <h1 className="m-0 mb-4 text-[22px] text-zephyr-text">System Setup</h1>

      {/* Tab bar */}
      <div className="flex gap-0 mb-6 border-b-2 border-zephyr-border">
        {TABS.map(function (t) {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={function () {
                setTab(t.key);
              }}
              className={[
                "bg-transparent border-0 py-[10px] px-5 text-[14px] cursor-pointer -mb-[2px]",
                active
                  ? "border-b-[3px] border-b-zephyr-teal font-bold text-zephyr-teal"
                  : "border-b-[3px] border-b-transparent font-normal text-zephyr-muted",
              ].join(" ")}
              style={{ fontFamily: "inherit" }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="max-w-[900px]">
        {/* ---------------------------------------------------------------- */}
        {/* Alerts */}
        {/* ---------------------------------------------------------------- */}
        {tab === "alerts" && (
          <>
            <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
              When a notification email (to a shelter, the invoicing team, or a task assignee) fails
              to send, the portal records it in the audit log and sends an alert to this address. No
              request data is ever lost when an email fails — only the notification doesn't go out.
            </p>
            <label
              htmlFor="system-alert-email"
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              System Alert Email Address
            </label>
            <input
              id="system-alert-email"
              type="email"
              value={alertEmail}
              disabled={!alertEditing}
              onChange={function (e) {
                setAlertEmail(e.target.value);
              }}
              className="w-full max-w-[480px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />
            <EditSaveFooter
              editing={alertEditing}
              onEdit={function () {
                alertSnap.current = alertEmail;
                setAlertMsg("");
                setAlertEditing(true);
              }}
              onSave={saveSystemAlert}
              onCancel={function () {
                setAlertEmail(alertSnap.current);
                setAlertMsg("");
                setAlertEditing(false);
              }}
              saving={alertSaving}
              msg={alertMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            {/* Failed-notifications list — moved here from the System Content →
                "Monitoring" tab (26 Jul 2026). */}
            <FailedNotificationsList />
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Access — role-group login lockout */}
        {/* ---------------------------------------------------------------- */}
        {tab === "access" && (
          <>
            <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
              Turn a switch ON to stop that group signing in. A locked-out person is refused at
              login and signed out on their next page load, and shown the message below. SuperUsers
              are never locked out, so you can always get back in to turn a switch off.
            </p>

            <SlideToggle
              checked={lockout.shelter}
              disabled={!lockoutEditing}
              onChange={function (v) {
                setLockoutField("shelter", v);
              }}
              label="Lockout all DFV Services"
            />
            <SlideToggle
              checked={lockout.picker}
              disabled={!lockoutEditing}
              onChange={function (v) {
                setLockoutField("picker", v);
              }}
              label="Lockout all Zephyr Pickers"
            />
            <SlideToggle
              checked={lockout.responder}
              disabled={!lockoutEditing}
              onChange={function (v) {
                setLockoutField("responder", v);
              }}
              label="Lockout all Zephyr Responders"
            />
            <SlideToggle
              checked={lockout.supervisor}
              disabled={!lockoutEditing}
              onChange={function (v) {
                setLockoutField("supervisor", v);
              }}
              label="Lockout all Zephyr Supervisors"
            />

            <p className="m-0 mt-2 mb-2 text-[12px] text-zephyr-muted italic">
              A Combined user (Picker + Responder) is locked out whenever either the Pickers or the
              Responders switch is on.
            </p>

            <hr className="my-6 border-0 border-t border-zephyr-border" />

            <label
              htmlFor="access-lockout-message"
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              Lockout Message (shown to a locked-out user)
            </label>
            <textarea
              id="access-lockout-message"
              value={lockout.message}
              disabled={!lockoutEditing}
              onChange={function (e) {
                setLockoutField("message", e.target.value);
              }}
              rows={3}
              style={{ fontFamily: "inherit" }}
              className="w-full max-w-[600px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />

            <EditSaveFooter
              editing={lockoutEditing}
              onEdit={function () {
                lockoutSnap.current = lockout;
                setLockoutMsg("");
                setLockoutEditing(true);
              }}
              onSave={saveLockout}
              onCancel={function () {
                if (lockoutSnap.current) setLockout(lockoutSnap.current);
                setLockoutMsg("");
                setLockoutEditing(false);
              }}
              saving={lockoutSaving}
              msg={lockoutMsg}
            />

            <hr className="my-6 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">New Requests</h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              Turn this OFF to close intake — the <strong>+ New Request</strong> button disappears
              for DFV Services. Existing <strong>drafts can still be finished and submitted</strong>
              , and everything else (viewing, comments, files) is unaffected.
            </p>
            <SlideToggle
              checked={newRequests}
              onChange={saveNewRequests}
              label="Allow DFV Services to create new requests"
            />
            {newRequestsMsg && (
              <div
                className={`mt-2 mb-2 text-[13px] font-semibold ${
                  newRequestsMsg.startsWith("Save failed") ? "text-[#C62828]" : "text-[#2E7D32]"
                }`}
              >
                {newRequestsMsg}
              </div>
            )}

            <hr className="my-6 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">Stock Top-ups</h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              Controls whether DFV Services can raise a <strong>Stock Top-up</strong> request (bulk
              stationery, bags, hats, socks…). Only services with <strong>Stock Held</strong> turned
              on are offered the New-Request type chooser at all. When this is <strong>off</strong>,
              those services still see the chooser but the Stock Top-up option is disabled and reads
              <strong>"Not available at the moment"</strong>. Turn it <strong>on</strong> to make
              Stock Top-ups available to all DFV Services.
            </p>
            <SlideToggle
              checked={stockTopUp}
              onChange={toggleStockTopUp}
              label="Make Stock Top-ups available to all DFV Services with STOCK HELD turned ON"
            />
            {stuSaving && <span className="text-[13px] text-zephyr-muted">Saving…</span>}
            {stuMsg && (
              <span
                className={`text-[13px] font-semibold ${
                  stuMsg.startsWith("Save failed") ? "text-[#C62828]" : "text-[#2E7D32]"
                }`}
              >
                {stuMsg}
              </span>
            )}

            <hr className="my-6 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Login Verification (CAPTCHA)
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              Shows a Cloudflare "verify you are human" box on the login screen to block automated
              password-guessing. This switch hides or shows that box.
              {captchaEnabled
                ? ""
                : " (No effect in this environment yet — no Turnstile site key is configured here.)"}
            </p>
            <div
              className="mb-3 text-[13px] rounded-[8px] p-3"
              style={{ background: "#FFF8E1", border: "1px solid #FFE082", color: "#7A5B00" }}
            >
              <strong>To switch CAPTCHA OFF, do the Supabase step FIRST.</strong> This switch only
              hides the box on the login screen — the actual checking runs in Supabase. If you hide
              the box while Supabase is still enforcing it,{" "}
              <strong>every sign-in is rejected and nobody can log in.</strong> Turn it off in{" "}
              <strong>Supabase &rarr; Authentication &rarr; Attack Protection</strong> (set CAPTCHA
              protection off and Save) <em>before</em> turning this switch off.
            </div>
            <SlideToggle
              checked={captchaOn}
              onChange={function (v) {
                if (v) turnCaptchaOn();
                else turnCaptchaOff();
              }}
              label="Show the login verification box"
            />
            {captchaBusy && <span className="text-[13px] text-zephyr-muted">Saving…</span>}
            {captchaMsg && (
              <span
                className={`text-[13px] font-semibold ${
                  captchaMsg.startsWith("Failed") ? "text-[#C62828]" : "text-[#2E7D32]"
                }`}
              >
                {captchaMsg}
              </span>
            )}
            {/* Possible-repeat flag settings (db-210) — self-contained component. */}
            <RepeatCheckSettings />
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Config (placeholder) */}
        {/* ---------------------------------------------------------------- */}
        {tab === "config" && (
          <>
            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">Next Request Number</h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              Every submitted request is given the next number in sequence (e.g.{" "}
              <strong>2026-00211</strong>). You normally never need to touch this. On{" "}
              <strong>go-live</strong>, set it to the number your current platform would issue next,
              so the new portal carries straight on. The year is added automatically — you only set
              the number.
            </p>
            <div className="text-[13px] text-zephyr-text mb-3">
              The next request issued will be:{" "}
              <strong className="text-zephyr-teal">{refPreview || "—"}</strong>
            </div>
            <label
              htmlFor="next-ref-no"
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              Set the Next Request Number
            </label>
            <input
              id="next-ref-no"
              type="text"
              value={refInput}
              placeholder="2026-00211"
              disabled={!refEditing}
              onChange={function (e) {
                setRefInput(e.target.value);
              }}
              className="w-full max-w-[200px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />
            <div className="flex gap-[12px] mt-[14px] items-center">
              {!refEditing ? (
                <Btn
                  onClick={function () {
                    setRefInput(refPreview);
                    setRefMsg("");
                    setRefEditing(true);
                  }}
                >
                  Edit
                </Btn>
              ) : (
                <>
                  <Btn onClick={saveNextRefNo} disabled={refSaving}>
                    {refSaving ? "Saving..." : "Save"}
                  </Btn>
                  <button
                    type="button"
                    onClick={function () {
                      setRefInput(refPreview);
                      setRefMsg("");
                      setRefEditing(false);
                    }}
                    className="bg-white text-zephyr-text rounded-[8px] px-4 py-[7px] text-[13px] font-semibold cursor-pointer"
                    style={{ border: "1px solid #CFE0DE" }}
                  >
                    Cancel
                  </button>
                </>
              )}
              {refMsg && (
                <span
                  className={`text-[13px] font-semibold ${
                    refMsg.startsWith("Save failed") || refMsg.startsWith("Enter")
                      ? "text-[#C62828]"
                      : "text-[#2E7D32]"
                  }`}
                >
                  {refMsg}
                </span>
              )}
            </div>

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Automatic Logout (inactivity)
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              After this many minutes of no activity, a user is automatically signed out (any
              in-progress request draft is saved first, and a warning appears shortly before — up to{" "}
              <strong>5 minutes</strong>, or sooner on a short period). You can set a different
              period for DFV Services and for Zephyr staff.
            </p>
            <div className="grid gap-4 mb-1" style={{ gridTemplateColumns: "220px 220px" }}>
              <div>
                <label
                  htmlFor="timeout-shelter"
                  className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
                >
                  DFV Services (minutes)
                </label>
                <input
                  id="timeout-shelter"
                  type="number"
                  min={MIN_MINUTES}
                  max={MAX_MINUTES}
                  step="1"
                  value={timeout.shelter_minutes}
                  disabled={!timeoutEditing}
                  onChange={function (e) {
                    setTimeoutField("shelter_minutes", e.target.value);
                  }}
                  className="w-[120px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                />
              </div>
              <div>
                <label
                  htmlFor="timeout-staff"
                  className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
                >
                  Zephyr staff (minutes)
                </label>
                <input
                  id="timeout-staff"
                  type="number"
                  min={MIN_MINUTES}
                  max={MAX_MINUTES}
                  step="1"
                  value={timeout.staff_minutes}
                  disabled={!timeoutEditing}
                  onChange={function (e) {
                    setTimeoutField("staff_minutes", e.target.value);
                  }}
                  className="w-[120px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                />
              </div>
            </div>
            <EditSaveFooter
              editing={timeoutEditing}
              onEdit={function () {
                timeoutSnap.current = timeout;
                setTimeoutMsg("");
                setTimeoutEditing(true);
              }}
              onSave={saveTimeout}
              onCancel={function () {
                if (timeoutSnap.current) setTimeoutCfg(timeoutSnap.current);
                setTimeoutMsg("");
                setTimeoutEditing(false);
              }}
              saving={timeoutSaving}
              saveLabel="Save Logout Times"
              msg={timeoutMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Per-Device Delivery Address
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              Each request now has its own delivery address, so the separate{" "}
              <strong>Delivery Address</strong> box shown on each Device item is usually not needed.
              Leave this <strong>off</strong> to hide it on the request form, the submitted-request
              views, and the picker screen. Hiding it doesn't change where anything is delivered —
              devices always use the request's delivery address. Turn it on again if you decide you
              need a separate delivery address per device.
            </p>
            <SlideToggle
              checked={showDeviceDelivery}
              onChange={toggleShowDeviceDelivery}
              label="Show the per-device delivery address on Device items"
            />
            {sddSaving && <span className="text-[13px] text-zephyr-muted">Saving…</span>}
            {sddMsg && (
              <span
                className={`text-[13px] font-semibold ${
                  sddMsg.startsWith("Save failed") ? "text-[#C62828]" : "text-[#2E7D32]"
                }`}
              >
                {sddMsg}
              </span>
            )}
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Feedback Module */}
        {/* ---------------------------------------------------------------- */}
        {tab === "feedback" && (
          <>
            <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
              When a shelter worker submits feedback, the portal emails a notification with a link
              to the feedback at this address.
            </p>
            <label
              htmlFor="feedback-notify-email"
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              Feedback Notification Email Address
            </label>
            <input
              id="feedback-notify-email"
              type="email"
              value={fbEmail}
              disabled={!fbEditing}
              onChange={function (e) {
                setFbEmail(e.target.value);
              }}
              className="w-full max-w-[480px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />
            <EditSaveFooter
              editing={fbEditing}
              onEdit={function () {
                fbSnap.current = fbEmail;
                setFbMsg("");
                setFbEditing(true);
              }}
              onSave={saveFeedbackNotify}
              onCancel={function () {
                setFbEmail(fbSnap.current);
                setFbMsg("");
                setFbEditing(false);
              }}
              saving={fbSaving}
              msg={fbMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
              When an admin marks a piece of feedback as reviewed, the portal opens a reply they can
              edit before sending it to the shelter worker who lodged it. This is the standard reply
              text used as the starting point.
            </p>
            <label
              htmlFor="feedback-review-template"
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              Feedback Review Reply (standard text)
            </label>
            <textarea
              id="feedback-review-template"
              value={reviewTpl}
              disabled={!reviewEditing}
              onChange={function (e) {
                setReviewTpl(e.target.value);
              }}
              rows={6}
              style={{ fontFamily: "inherit" }}
              className="w-full max-w-[600px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />
            <EditSaveFooter
              editing={reviewEditing}
              onEdit={function () {
                reviewSnap.current = reviewTpl;
                setReviewMsg("");
                setReviewEditing(true);
              }}
              onSave={saveReviewTemplate}
              onCancel={function () {
                setReviewTpl(reviewSnap.current);
                setReviewMsg("");
                setReviewEditing(false);
              }}
              saving={reviewSaving}
              msg={reviewMsg}
            />
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* School Locker */}
        {/* ---------------------------------------------------------------- */}
        {tab === "school_locker" && (
          <>
            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              School Locker Order Recipient
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              When a School Locker order is submitted it's emailed to School Locker's external
              service desk. This is the address those orders are sent to — change it if School
              Locker gives you a new inbox.
              <br />
              <strong>Testing</strong> is done on{" "}
              <strong>tosd@theschoollocker.atlassian.net</strong>; <strong>Live Production</strong>{" "}
              is on <strong>orders@theschoollocker.com.au</strong>.
            </p>
            <label
              htmlFor="sl-order-email"
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              Order Recipient Email
            </label>
            {slOrderEditing ? (
              <input
                id="sl-order-email"
                type="email"
                value={slOrderEmail}
                placeholder="orders@example.com"
                onChange={function (e) {
                  setSlOrderEmail(e.target.value);
                }}
                className="w-full max-w-[480px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white"
              />
            ) : (
              <div
                id="sl-order-email"
                className="w-full max-w-[480px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-zephyr-off-white"
              >
                {slOrderEmail || <span className="text-zephyr-muted italic">Not set</span>}
              </div>
            )}
            <div className="flex gap-[12px] mt-[14px] items-center">
              {slOrderEditing ? (
                <>
                  <Btn onClick={saveSlOrder} disabled={slOrderSaving}>
                    {slOrderSaving ? "Saving..." : "Save Order Recipient"}
                  </Btn>
                  <Btn
                    onClick={function () {
                      setSlOrderEmail(slOrderSaved);
                      setSlOrderEditing(false);
                      setSlOrderMsg("");
                    }}
                    disabled={slOrderSaving}
                    variant="secondary"
                  >
                    Cancel
                  </Btn>
                </>
              ) : (
                <Btn
                  onClick={function () {
                    setSlOrderEditing(true);
                    setSlOrderMsg("");
                  }}
                >
                  Edit
                </Btn>
              )}
              {slOrderMsg && (
                <span
                  className={`text-[13px] font-semibold ${
                    slOrderMsg.startsWith("Save failed") || slOrderMsg.startsWith("Please")
                      ? "text-[#C62828]"
                      : "text-[#2E7D32]"
                  }`}
                >
                  {slOrderMsg}
                </span>
              )}
            </div>

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
              Because the order email goes to an outside integration, you can also receive a blind
              copy (BCC) of every order at a monitoring address so you can confirm orders are still
              going out. Turn this off once you're confident the connection is reliable.
            </p>

            <label className="flex items-center gap-[10px] mb-4 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={slEnabled}
                disabled={!bccEditing}
                onChange={function (e) {
                  setSlEnabled(e.target.checked);
                }}
                className="w-[18px] h-[18px] cursor-pointer disabled:cursor-not-allowed"
              />
              <span className="text-[14px] text-zephyr-text font-semibold">
                Send me a BCC copy of every School Locker order
              </span>
            </label>

            <label
              htmlFor="sl-bcc-email"
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              Monitoring Email Address (BCC)
            </label>
            <input
              id="sl-bcc-email"
              type="email"
              value={slEmail}
              disabled={!bccEditing || !slEnabled}
              placeholder={
                slEnabled ? "name@example.com" : "Enable the copy above to set an address"
              }
              onChange={function (e) {
                setSlEmail(e.target.value);
              }}
              className="w-full max-w-[480px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />
            <EditSaveFooter
              editing={bccEditing}
              onEdit={function () {
                bccSnap.current = { enabled: slEnabled, email: slEmail };
                setSavedMsg("");
                setBccEditing(true);
              }}
              onSave={saveSlBcc}
              onCancel={function () {
                setSlEnabled(bccSnap.current.enabled);
                setSlEmail(bccSnap.current.email);
                setSavedMsg("");
                setBccEditing(false);
              }}
              saving={saving}
              msg={savedMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              School Locker No-Reply Alert
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              If a School Locker order gets no reply within the timeout window, the polling utility
              sends a no-reply alert email. This is the address that alert is sent to.
            </p>
            <label
              htmlFor="sl-alert-email"
              className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
            >
              Alert Recipient Email
            </label>
            <input
              id="sl-alert-email"
              type="email"
              value={slAlertEmail}
              disabled={!slAlertEditing}
              placeholder="name@example.com"
              onChange={function (e) {
                setSlAlertEmail(e.target.value);
              }}
              className="w-full max-w-[480px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />
            <EditSaveFooter
              editing={slAlertEditing}
              onEdit={function () {
                slAlertSnap.current = slAlertEmail;
                setSlAlertMsg("");
                setSlAlertEditing(true);
              }}
              onSave={saveSlAlert}
              onCancel={function () {
                setSlAlertEmail(slAlertSnap.current);
                setSlAlertMsg("");
                setSlAlertEditing(false);
              }}
              saving={slAlertSaving}
              saveLabel="Save Recipient"
              msg={slAlertMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              School Locker Polling
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              The polling utility watches the School Locker inbox and records the OSD code from each
              reply. It runs automatically every <strong>60 seconds</strong>, but only does real
              work while an order is actually waiting for a reply — when nothing is pending it stays
              idle to save resources, and a newly submitted order wakes it automatically. Use the
              button below to run a check immediately (for example, to test the connection).
            </p>
            <div className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] mb-1">
              Polling interval
            </div>
            <div className="text-[14px] text-zephyr-text mb-4">60 seconds (fixed)</div>
            <div className="flex gap-[12px] items-center">
              <Btn onClick={runPollingNow} disabled={pollRunning}>
                {pollRunning ? "Running..." : "Run polling now"}
              </Btn>
              {pollMsg && (
                <span
                  className={`text-[13px] font-semibold ${
                    pollMsg.startsWith("Run failed") ? "text-[#C62828]" : "text-[#2E7D32]"
                  }`}
                >
                  {pollMsg}
                </span>
              )}
            </div>
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Unleashed */}
        {/* ---------------------------------------------------------------- */}
        {tab === "unleashed" && (
          <>
            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Unleashed API — Sales Order (SO) Push
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              When ON, approved sales orders are pushed to Unleashed automatically. Keep this{" "}
              <strong>OFF</strong> while you validate orders by manual CSV import (Manage →
              Unleashed → Sales Orders → Export CSV); turn it on to go live. Turning it on lets you
              choose whether the orders already queued should be sent by the API or left out because
              you imported them manually. Excluded orders can always be released again from the
              Sales Orders screen. This switch controls{" "}
              <strong>only the automatic Sales Order push</strong> — the Customer and reference Sync
              Utilities (Manage → Unleashed → Sync Utilities) are separate and unaffected.
            </p>
            <div className="flex gap-[16px] items-center">
              <SlideToggle
                checked={apiEnabled}
                disabled={apiBusy}
                size="lg"
                onChange={function (v) {
                  if (v) turnApiOn();
                  else turnApiOff();
                }}
                label={apiEnabled ? "Sales Order push: ON" : "Sales Order push: OFF"}
              />
              {apiMsg && (
                <span
                  className={`text-[13px] font-semibold ${
                    apiMsg.startsWith("Failed") ? "text-[#C62828]" : "text-[#2E7D32]"
                  }`}
                >
                  {apiMsg}
                </span>
              )}
            </div>

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Unleashed API — Customer Auto-Sync
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              When ON, changes to a <strong>Zephyr</strong> DFV Service (details, bank details, and
              delivery addresses) are pushed to Unleashed automatically by a background worker. TEST
              / non-Zephyr services are never auto-pushed. Keep this <strong>OFF</strong> until
              go-live — changes made while it's off are queued and sent on the next run once you
              turn it on (nothing is lost). This is separate from the Sales Order push above and
              from the manual Customer sync utilities.
            </p>
            <div className="flex gap-[16px] items-center">
              <SlideToggle
                checked={custSyncEnabled}
                disabled={custSyncBusy}
                size="lg"
                onChange={function (v) {
                  if (v) turnCustSyncOn();
                  else turnCustSyncOff();
                }}
                label={custSyncEnabled ? "Customer auto-sync: ON" : "Customer auto-sync: OFF"}
              />
              {custSyncMsg && (
                <span
                  className={`text-[13px] font-semibold ${
                    custSyncMsg.startsWith("Failed") ? "text-[#C62828]" : "text-[#2E7D32]"
                  }`}
                >
                  {custSyncMsg}
                </span>
              )}
            </div>

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">Push Failure Alerts</h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              Choose which alerts fire when a Sales Order permanently fails to push to Unleashed, or
              an authentication error blocks all pushes. Each channel can be turned off
              independently.
            </p>
            {[
              ["email", "Email the system alert address"],
              ["banner", "Show a banner to supervisors and above"],
              ["audit", "Write failure audit-log records"],
              ["runLog", "Write a run-log row each push run"],
            ].map(function (row) {
              const key = row[0];
              return (
                <label
                  key={key}
                  className="flex items-center gap-[10px] mb-2 cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={alerts[key]}
                    disabled={!alertsEditing}
                    onChange={function (e) {
                      const checked = e.target.checked;
                      setAlerts(function (a) {
                        return { ...a, [key]: checked };
                      });
                    }}
                    className="w-[18px] h-[18px] cursor-pointer disabled:cursor-not-allowed"
                  />
                  <span className="text-[14px] text-zephyr-text">{row[1]}</span>
                </label>
              );
            })}
            <EditSaveFooter
              editing={alertsEditing}
              onEdit={function () {
                alertsSnap.current = alerts;
                setAlertsMsg("");
                setAlertsEditing(true);
              }}
              onSave={saveAlerts}
              onCancel={function () {
                if (alertsSnap.current) setAlerts(alertsSnap.current);
                setAlertsMsg("");
                setAlertsEditing(false);
              }}
              saving={alertsSaving}
              saveLabel="Save Alerts"
              msg={alertsMsg}
            />
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* AI Searches — editable BYOD lookup prompt + annual policy-review date */}
        {/* ---------------------------------------------------------------- */}
        {tab === "ai_searches" && (
          <>
            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              BYOD School Lookup — AI Prompt
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              This is the instruction sent to the AI when a responder looks up a school's
              Bring-Your-Own-Device (BYOD) policy. Tune the wording here without a code change. The
              placeholders <strong>{"{school}"}</strong>, <strong>{"{location}"}</strong> and{" "}
              <strong>{"{yearLevel}"}</strong> are filled in for each lookup. The exact list of
              fields the AI must return (status, device, the year-level band it covers — as a label
              plus a numeric from/to range, loan, hardship, policy URL, notes, confidence) is fixed
              and added automatically, so you can't break the format here. Only the school's name,
              suburb, state and the student's year level are ever sent — no personal data. Leave it
              blank to restore the built-in default.
            </p>
            <textarea
              id="byod-prompt"
              value={byodPrompt}
              disabled={!byodPromptEditing}
              onChange={function (e) {
                setByodPrompt(e.target.value);
              }}
              rows={12}
              style={{ fontFamily: "inherit" }}
              className="w-full max-w-[760px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[13px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />
            {byodPromptEditing && (
              <div className="mt-1">
                <button
                  type="button"
                  onClick={function () {
                    setByodPrompt(DEFAULT_BYOD_PROMPT);
                  }}
                  className="bg-transparent border-0 text-zephyr-teal text-[12px] font-semibold cursor-pointer underline p-0"
                >
                  Restore default wording
                </button>
              </div>
            )}
            <EditSaveFooter
              editing={byodPromptEditing}
              onEdit={function () {
                byodPromptSnap.current = byodPrompt;
                setByodPromptMsg("");
                setByodPromptEditing(true);
              }}
              onSave={saveByodPrompt}
              onCancel={function () {
                setByodPrompt(byodPromptSnap.current);
                setByodPromptMsg("");
                setByodPromptEditing(false);
              }}
              saving={byodPromptSaving}
              saveLabel="Save Prompt"
              msg={byodPromptMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              BYOD Whole-School Lookup — AI Prompt
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              This is the instruction used when an admin looks up a whole school's BYOD policy from
              the Edit-School screen (every year band Prep–12 at once, rather than one child's
              year). The placeholders <strong>{"{school}"}</strong> and{" "}
              <strong>{"{location}"}</strong> are filled in per lookup. The fixed output format — a
              list of bands, each with status, device, its year from/to range, loan, hardship,
              policy URL and notes — is added automatically, so you can't break it here. Only the
              school's name, suburb and state are sent. Leave it blank to restore the built-in
              default.
            </p>
            <textarea
              id="byod-bands-prompt"
              value={byodBandsPrompt}
              disabled={!byodBandsPromptEditing}
              onChange={function (e) {
                setByodBandsPrompt(e.target.value);
              }}
              rows={12}
              style={{ fontFamily: "inherit" }}
              className="w-full max-w-[760px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[13px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            />
            {byodBandsPromptEditing && (
              <div className="mt-1">
                <button
                  type="button"
                  onClick={function () {
                    setByodBandsPrompt(DEFAULT_BYOD_BANDS_PROMPT);
                  }}
                  className="bg-transparent border-0 text-zephyr-teal text-[12px] font-semibold cursor-pointer underline p-0"
                >
                  Restore default wording
                </button>
              </div>
            )}
            <EditSaveFooter
              editing={byodBandsPromptEditing}
              onEdit={function () {
                byodBandsPromptSnap.current = byodBandsPrompt;
                setByodBandsPromptMsg("");
                setByodBandsPromptEditing(true);
              }}
              onSave={saveByodBandsPrompt}
              onCancel={function () {
                setByodBandsPrompt(byodBandsPromptSnap.current);
                setByodBandsPromptMsg("");
                setByodBandsPromptEditing(false);
              }}
              saving={byodBandsPromptSaving}
              saveLabel="Save Prompt"
              msg={byodBandsPromptMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Annual Policy-Review Date
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              Schools usually publish next year's BYOD policy around this date. A lookup done on or
              after it is recorded against the <strong>next</strong> school year, and a stored
              result from before the most recent one is treated as possibly out of date. Default is{" "}
              <strong>15 December</strong>.
            </p>
            <div className="grid gap-4 mb-1" style={{ gridTemplateColumns: "220px 120px" }}>
              <div>
                <label
                  htmlFor="policy-review-month"
                  className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
                >
                  Month
                </label>
                <select
                  id="policy-review-month"
                  value={policyReview.month}
                  disabled={!policyReviewEditing}
                  onChange={function (e) {
                    setPolicyReviewField("month", Number(e.target.value));
                  }}
                  className="w-full py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                >
                  {MONTHS.map(function (m, i) {
                    return (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div>
                <label
                  htmlFor="policy-review-day"
                  className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
                >
                  Day
                </label>
                <input
                  id="policy-review-day"
                  type="number"
                  min={1}
                  max={31}
                  step="1"
                  value={policyReview.day}
                  disabled={!policyReviewEditing}
                  onChange={function (e) {
                    setPolicyReviewField("day", e.target.value);
                  }}
                  className="w-[100px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                />
              </div>
            </div>
            <EditSaveFooter
              editing={policyReviewEditing}
              onEdit={function () {
                policyReviewSnap.current = policyReview;
                setPolicyReviewMsg("");
                setPolicyReviewEditing(true);
              }}
              onSave={savePolicyReview}
              onCancel={function () {
                if (policyReviewSnap.current) setPolicyReview(policyReviewSnap.current);
                setPolicyReviewMsg("");
                setPolicyReviewEditing(false);
              }}
              saving={policyReviewSaving}
              saveLabel="Save Review Date"
              msg={policyReviewMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">AI Thinking Level</h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              How hard the AI reasons before answering a lookup. <strong>Low</strong> is fastest and
              cheapest but answers can be thin; <strong>High</strong> is the most thorough (slower,
              more tokens); <strong>Medium</strong> is the balanced default. The reasoning counts
              toward the token cost, so a higher level costs a little more per lookup.
            </p>
            <select
              id="byod-thinking"
              value={byodThinking}
              disabled={!byodThinkingEditing}
              onChange={function (e) {
                setByodThinking(e.target.value);
              }}
              className="w-[280px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
            >
              <option value="low">Low (fastest / cheapest)</option>
              <option value="medium">Medium (balanced — default)</option>
              <option value="high">High (most thorough)</option>
            </select>
            <EditSaveFooter
              editing={byodThinkingEditing}
              onEdit={function () {
                byodThinkingSnap.current = byodThinking;
                setByodThinkingMsg("");
                setByodThinkingEditing(true);
              }}
              onSave={saveByodThinking}
              onCancel={function () {
                setByodThinking(byodThinkingSnap.current);
                setByodThinkingMsg("");
                setByodThinkingEditing(false);
              }}
              saving={byodThinkingSaving}
              saveLabel="Save Thinking Level"
              msg={byodThinkingMsg}
            />

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Show the BYOD Lookup Button
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              Whether the <strong>BYOD AI</strong> lookup button appears for responders (on a
              request's Device card) and for shelters (on the request form's device step). It only
              ever shows for a child whose school was chosen from the list. Both are{" "}
              <strong>off</strong> by default — turn them on once schools have been seeded with BYOD
              data. Each switch saves immediately.
            </p>
            <div className="flex flex-col gap-2">
              <div>
                <SlideToggle
                  checked={byodShowResp}
                  onChange={toggleByodShowResp}
                  label="Show BYOD lookup to Responders"
                />
                {byodShowRespSaving && (
                  <span className="ml-2 text-[13px] text-zephyr-muted">Saving…</span>
                )}
                {byodShowRespMsg && (
                  <span
                    className={`ml-2 text-[13px] font-semibold ${
                      byodShowRespMsg.startsWith("Save failed")
                        ? "text-[#C62828]"
                        : "text-[#2E7D32]"
                    }`}
                  >
                    {byodShowRespMsg}
                  </span>
                )}
              </div>
              <div>
                <SlideToggle
                  checked={byodShowShel}
                  onChange={toggleByodShowShel}
                  label="Show BYOD lookup to Shelters"
                />
                {byodShowShelSaving && (
                  <span className="ml-2 text-[13px] text-zephyr-muted">Saving…</span>
                )}
                {byodShowShelMsg && (
                  <span
                    className={`ml-2 text-[13px] font-semibold ${
                      byodShowShelMsg.startsWith("Save failed")
                        ? "text-[#C62828]"
                        : "text-[#2E7D32]"
                    }`}
                  >
                    {byodShowShelMsg}
                  </span>
                )}
              </div>
            </div>

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Let Responders Edit BYOD Results
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              When <strong>off</strong> (default), a responder's BYOD lookup saves the result
              automatically and shows the band for that child <strong>read-only</strong> — any
              correction is made by an admin on the school's Edit screen. When <strong>on</strong>,
              responders can review, edit and save the result themselves before it's stored. This is
              separate from the show/hide switches above and the search-scope switch below. Saves
              immediately.
            </p>
            <div>
              <SlideToggle
                checked={byodRespEdit}
                onChange={toggleByodRespEdit}
                label="Allow responders to edit/save BYOD results"
              />
              {byodRespEditSaving && (
                <span className="ml-2 text-[13px] text-zephyr-muted">Saving…</span>
              )}
              {byodRespEditMsg && (
                <span
                  className={`ml-2 text-[13px] font-semibold ${
                    byodRespEditMsg.startsWith("Save failed") ? "text-[#C62828]" : "text-[#2E7D32]"
                  }`}
                >
                  {byodRespEditMsg}
                </span>
              )}
            </div>

            <hr className="my-7 border-0 border-t border-zephyr-border" />

            <h3 className="m-0 mb-2 text-[15px] font-bold text-zephyr-text">
              Responder Lookup Scope
            </h3>
            <p className="m-0 mb-3 text-[13px] text-zephyr-muted">
              When <strong>off</strong> (default), a responder's BYOD lookup searches only the{" "}
              <strong>current child's year</strong> (faster). When <strong>on</strong>, it searches
              the <strong>whole school</strong> at once (every year band), which fills the record
              for other year levels but can take longer. Either way a lookup saves any band not
              already on file, and a year already looked up is shown without a new search. Saves
              immediately.
            </p>
            <div>
              <SlideToggle
                checked={byodWholeSchool}
                onChange={toggleByodWholeSchool}
                label="Responder lookup searches the whole school"
              />
              {byodWholeSchoolSaving && (
                <span className="ml-2 text-[13px] text-zephyr-muted">Saving…</span>
              )}
              {byodWholeSchoolMsg && (
                <span
                  className={`ml-2 text-[13px] font-semibold ${
                    byodWholeSchoolMsg.startsWith("Save failed")
                      ? "text-[#C62828]"
                      : "text-[#2E7D32]"
                  }`}
                >
                  {byodWholeSchoolMsg}
                </span>
              )}
            </div>
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Section Availability (moved here from Manage, 2 Aug 2026) */}
        {/* ---------------------------------------------------------------- */}
        {tab === "section_availability" && <SectionAvailabilityAdmin embedded />}
      </div>
    </div>
  );
}
